import { useFocusEffect, useLocalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useLayoutEffect } from 'react';
import { FlatList, ListRenderItem, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetNPOLightRatingQuestionsAnswers } from '@/api/hooks/nonProfitOrgHooks';
import { NPOLightRatingQuestionAnswers } from '@/api/models/nonProfitOrgModels';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import { QuestionType } from '@/components/inputs/QuestionGroup';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

const NpoLightRatingQuestions = () => {
  const colors = useColor();
  const navigation = useNavigation();
  const { npoId, name, questionsOptions } = useLocalSearchParams<{
    npoId: string;
    name: string;
    questionsOptions: string;
  }>();
  const { data, isLoading, isRefetching, refetch } = useGetNPOLightRatingQuestionsAnswers({ id: npoId });

  const parsedQuestionOptions = safeJsonParse<QuestionType[]>(questionsOptions);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerShadowVisible: false,
      headerBackVisible: false,
      headerTitle: () => <Title label={name} />,
      headerLeft: () => <GoBackButton />
    });
  }, [name, navigation]);

  useFocusEffect(
    useCallback(() => {
      if (npoId) refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const QuestionItem: ListRenderItem<NPOLightRatingQuestionAnswers> = ({ item }) => {
    if (parsedQuestionOptions !== undefined) {
      return (
        <>
          <View style={styles.row}>
            <Text style={[styles.item, { color: colors.titleText }]} fontWeight="600">
              {i18n.t('question.question_list.question')}
            </Text>
            <Text style={[styles.value, { color: colors.titleText }]}>
              {parsedQuestionOptions!.find((option) => option.id === item.questionId)?.text ?? '-'}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={[styles.item, { color: colors.titleText }]} fontWeight="600">
              {i18n.t('question.question_list.answer')}
            </Text>
            <Text fontWeight="900" style={[styles.value, { color: colors.titleText }]}>
              {item.answer === true ? 'Yes' : item.answer === false ? 'No' : '-'}
            </Text>
          </View>
        </>
      );
    } else {
      return <></>;
    }
  };

  const orderMap = new Map(parsedQuestionOptions?.map((opt) => [opt.id, opt.order]));
  const sortedData = data?.sort((a, b) => {
    const orderA = orderMap.get(a.questionId) ?? 0;
    const orderB = orderMap.get(b.questionId) ?? 0;
    return orderA - orderB;
  });

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        {!isLoading && !isRefetching && data && parsedQuestionOptions !== undefined ? (
          <FlatList
            style={styles.flatList}
            contentContainerStyle={styles.listContainer}
            renderItem={QuestionItem}
            data={sortedData}
            onRefresh={refetch}
            refreshing={isRefetching}
            ListEmptyComponent={<Text style={styles.emptyText}> {i18n.t('mix.n_a')}</Text>}
          />
        ) : (
          <DotsLoader />
        )}
      </View>
    </SafeAreaView>
  );
};

export default NpoLightRatingQuestions;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },

  loadingContainer: {
    alignItems: 'center'
  },

  flatList: {
    width: '100%'
  },

  listContainer: {
    paddingHorizontal: 18,
    paddingBottom: 18,
    rowGap: 25
  },

  row: {
    flexDirection: 'row'
  },

  item: {
    minWidth: 70
  },

  value: {
    flex: 1
  },

  emptyText: {
    textAlign: 'center'
  }
});
